import {useCallback, useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {useEventCallback} from 'usehooks-ts';

import {fileSizeToString} from '../../../utils/attachments/utils';

export const useIsDragging = () => {
    const [isDragging, setIsDragging] = useState(false);

    const dragLeaveTimeoutId = useRef<NodeJS.Timeout | null>(null);

    const handleGlobalDragOver = useEventCallback(() => {
        if (dragLeaveTimeoutId.current) {
            clearTimeout(dragLeaveTimeoutId.current);
        }
        setIsDragging(true);
    });

    const handleGlobalDragLeave = useEventCallback(() => {
        const id = setTimeout(() => {
            setIsDragging(false);
        }, 100);
        dragLeaveTimeoutId.current = id;
    });

    const handleGlobalDrop = useEventCallback(() => {
        setIsDragging(false);
    });

    useEffect(() => {
        document.addEventListener('dragover', handleGlobalDragOver);
        document.addEventListener('dragleave', handleGlobalDragLeave);
        document.addEventListener('drop', handleGlobalDrop);

        return () => {
            document.removeEventListener('dragover', handleGlobalDragOver);
            document.removeEventListener('dragleave', handleGlobalDragLeave);
            document.removeEventListener('drop', handleGlobalDrop);
            if (dragLeaveTimeoutId.current) {
                clearTimeout(dragLeaveTimeoutId.current);
            }
        };
    }, [handleGlobalDragOver, handleGlobalDragLeave, handleGlobalDrop]);

    return isDragging;
};

type ValidationRules = {
    maxImageSize: number;
    minImageWidth?: number;
    minImageHeight?: number;
    extensions: string[];
};

export const useValidation = ({
    maxImageSize,
    extensions,
    minImageWidth,
    minImageHeight,
}: ValidationRules) => {
    const {formatMessage} = useIntl();

    const validate = useCallback(async (file: File) => {
        if (!extensions.includes(file.type) || file.size > maxImageSize) {
            return formatMessage({
                id: 'form_upload_image.formats_and_size_error',
                defaultMessage: 'Choose a different photo format, you can use {formats} up to {size}',
            }, {
                size: fileSizeToString(maxImageSize),
                formats: extensions.
                    map((e) => e.replace('image/', '').toUpperCase()).
                    join(', '),
            });
        }

        if (minImageWidth && minImageHeight) {
            const isValid = await new Promise((resolve) => {
                const url = URL.createObjectURL(file);
                const img = new Image();

                img.onload = () => {
                    const isValid = img.width >= minImageWidth && img.height >= minImageHeight;
                    URL.revokeObjectURL(img.src);
                    resolve(isValid);
                };

                img.src = url;
            });

            if (!isValid) {
                return formatMessage({
                    id: 'form_upload_image.dimensions_error',
                    defaultMessage: 'Choose a different photo, you can use one that’s larger than {width} x {height} pixels',
                }, {
                    width: minImageWidth,
                    height: minImageHeight,
                });
            }
        }

        return undefined;
    }, [extensions, formatMessage, maxImageSize, minImageHeight, minImageWidth]);

    return validate;
};
